/**
 * 投稿モデレーション地図ビュー (Admin Wave AW5、docs/108 投稿モデレーション High)。
 *
 * old2 `apps/admin/src/pages/PostsModerationPage.tsx` の ModerationMap (L1000-1090) を
 * v2 admin に逐語移植:
 *   - MapLibre + 地理院タイル (淡色) raster tile (style version 8)
 *   - visibility 別マーカー色 (公開=緑 / 本人のみ=橙 / 全員非表示=赤)
 *   - マーカー差分更新 (visibility 変更で再生成) + 初回 fitBounds
 *   - マーカークリックで詳細モーダル (onSelectPost)
 *   - 左下に凡例 (色 → ラベル)
 *
 * v2 との差分:
 *   - maplibre-gl を admin の依存に追加 (user app は既存依存、admin は本 wave で新規)。
 *   - PostRow → v2 `ModerationPost` (latitude/longitude/visibility/title を使用)。
 *   - マーカー DOM element は document.createElement + cssText (maplibre Marker の慣用、
 *     React JSX 外の imperative API のため inline style 規約の対象外)。
 */
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useEffect, useRef } from 'react';
import type { PostVisibility } from '@citizen-walk/shared-types';
import type { ModerationPost } from './usePostsModeration';

const VISIBILITY_MARKER_COLOR: Record<PostVisibility, string> = {
  public: '#16A34A',
  hidden_others: '#D97706',
  hidden_all: '#DC2626',
};

const VISIBILITY_MARKER_LABEL: Record<PostVisibility, string> = {
  public: '公開',
  hidden_others: '本人のみ',
  hidden_all: '全員非表示',
};

export interface ModerationMapProps {
  posts: ModerationPost[];
  onSelectPost: (post: ModerationPost) => void;
}

export function ModerationMap({ posts, onSelectPost }: ModerationMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<Map<string, { marker: maplibregl.Marker; visibility: PostVisibility }>>(
    new Map(),
  );
  // onSelectPost を ref 経由で参照 (マーカー再生成を props 変化で誘発しない)。
  const onSelectRef = useRef(onSelectPost);
  useEffect(() => {
    onSelectRef.current = onSelectPost;
  }, [onSelectPost]);

  // 地図の初期化
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          gsi: {
            type: 'raster',
            tiles: ['https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'],
            tileSize: 256,
            maxzoom: 18,
            attribution: '地理院タイル',
          },
        },
        layers: [{ id: 'gsi', type: 'raster', source: 'gsi' }],
      },
      center: [139.767, 35.681],
      zoom: 12,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.on('load', () => map.resize());
    mapRef.current = map;
    const markers = markersRef.current;
    return () => {
      map.remove();
      mapRef.current = null;
      markers.clear();
    };
  }, []);

  // マーカーの差分更新
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const prev = markersRef.current;

    // 削除 or visibility 変更 → マーカー除去
    for (const [id, entry] of prev) {
      const post = posts.find((p) => p.id === id);
      if (!post || post.visibility !== entry.visibility) {
        entry.marker.remove();
        prev.delete(id);
      }
    }

    // 追加 + fitBounds 用座標収集
    const bounds = new maplibregl.LngLatBounds();
    let hasPoint = false;

    posts.forEach((p) => {
      if (p.latitude === 0 && p.longitude === 0) return;
      bounds.extend([p.longitude, p.latitude]);
      hasPoint = true;

      if (prev.has(p.id)) return;
      const color = VISIBILITY_MARKER_COLOR[p.visibility];
      const el = document.createElement('div');
      el.style.cssText = `width:20px;height:20px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3);cursor:pointer;`;
      el.title = `${VISIBILITY_MARKER_LABEL[p.visibility]} | ${p.title || '(無題)'}`;
      el.addEventListener('click', () => onSelectRef.current(p));
      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([p.longitude, p.latitude])
        .addTo(map);
      prev.set(p.id, { marker, visibility: p.visibility });
    });

    // 初回のみ fitBounds (全マーカーが揃ったタイミング)
    const placeable = posts.filter((p) => !(p.latitude === 0 && p.longitude === 0)).length;
    if (hasPoint && prev.size === placeable) {
      map.fitBounds(bounds, { padding: 60, maxZoom: 16 });
    }
  }, [posts]);

  return (
    <div className="relative">
      <div
        ref={containerRef}
        className="w-full h-[500px] rounded-lg border border-slate-200"
        data-testid="moderation-map"
      />
      <div className="absolute bottom-3 left-3 bg-white/90 rounded px-3 py-2 text-xs shadow space-y-0.5">
        {(['public', 'hidden_others', 'hidden_all'] as const).map((v) => (
          <div key={v} className="flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ background: VISIBILITY_MARKER_COLOR[v] }}
            />{' '}
            {VISIBILITY_MARKER_LABEL[v]}
          </div>
        ))}
      </div>
    </div>
  );
}
